import type { EvaluationState } from "@/hooks/queries/evaluation-types";

// Canonical evaluation-status mapping for Screens 02 + 12 (Story 6.9). ONE source of
// truth for the label↔pill↔state↔action map so the workspace home and the evaluations
// list never drift:
//
//   state        pill variant   label             list action
//   ----------   ------------   ---------------   -------------------------------------
//   draft        info           Draft             Continue → /evaluations/{id} (write)
//   submitted    pending        Ready to score    Open     → /evaluations/{id} (read)
//   scored       ok             Completed         Results  → /evaluations/{id} (read; Story 9.13)
//   archived     —              Archived          (excluded from the default list view)
//
// [Source: epics.md#1429-1433; 6-10 story Decision #2; ux-design-specification.md#812]

/** The Badge variant used to render a given evaluation state's status pill. */
export type EvaluationPillVariant = "info" | "pending" | "ok" | "neutral";

export interface EvaluationStatusMeta {
  /** Human label shown inside the pill (also the status-filter option text). */
  label: string;
  /** Badge variant token (clinical, desaturated). */
  pillVariant: EvaluationPillVariant;
}

export const EVALUATION_STATUS_META: Record<EvaluationState, EvaluationStatusMeta> = {
  draft: { label: "Draft", pillVariant: "info" },
  submitted: { label: "Ready to score", pillVariant: "pending" },
  scored: { label: "Completed", pillVariant: "ok" },
  // Archived is excluded from the default view; the map still covers it so a stray
  // archived row (e.g. an explicit ?status=archived) renders a neutral pill, not a crash.
  archived: { label: "Archived", pillVariant: "neutral" },
};

/** The per-row action kind, keyed off state. */
export type EvaluationActionKind = "continue" | "open" | "results";

export interface EvaluationAction {
  kind: EvaluationActionKind;
  /** Button/link label: Continue · Open · Results. */
  label: string;
  /**
   * A write action (resuming a draft) — disabled when the subscription is lapsed
   * (AC 13). Reads (Open / Results) stay available in read-only mode.
   */
  isWrite: boolean;
  /**
   * The action's destination screen is not built yet, so the control renders
   * disabled with a neutral tooltip rather than a dead link. Since Story 9.13
   * shipped the Results library (and the scored → results panel path), only
   * `archived` (View — an archived row may have no result) remains deferred.
   */
  isDeferred: boolean;
}

export const EVALUATION_ACTION: Record<EvaluationState, EvaluationAction> = {
  // Continue resumes the draft stepper (a write surface) → gated by lapsed subscription.
  draft: { kind: "continue", label: "Continue", isWrite: true, isDeferred: false },
  // Open the submitted evaluation at Review (read) → not gated.
  submitted: { kind: "open", label: "Open", isWrite: false, isDeferred: false },
  // Results library (Story 9.13) shipped → "Results" opens the scored evaluation at its
  // results step (/evaluations/{id}); a read, so it is never lapse-gated.
  scored: { kind: "results", label: "Results", isWrite: false, isDeferred: false },
  // Archived has no first-class action in the default list (excluded); fall back to a
  // (deferred) read so an explicitly-filtered archived row is never a dead link.
  archived: { kind: "results", label: "View", isWrite: false, isDeferred: true },
};

/**
 * The status-filter options for Screen 12 (label → canonical `?status=` value). "All
 * statuses" maps to `undefined` (the backend's default, archived-excluded view). The
 * three real states reuse the canonical labels so the filter and the pills agree.
 */
export interface StatusFilterOption {
  label: string;
  value: EvaluationState | undefined;
}

export const STATUS_FILTER_OPTIONS: readonly StatusFilterOption[] = [
  { label: "All statuses", value: undefined },
  { label: EVALUATION_STATUS_META.draft.label, value: "draft" },
  { label: EVALUATION_STATUS_META.submitted.label, value: "submitted" },
  { label: EVALUATION_STATUS_META.scored.label, value: "scored" },
];

/** Short, column-width labels for the rail steps (the list/home "Current step" cell). */
const STEP_SHORT_LABEL: Record<string, string> = {
  setup: "Setup",
  tasks: "Tasks",
  scoring: "Scoring",
  review: "Review",
  results: "Results",
};

/**
 * Humanize an evaluation's "Current step" cell. `current_step` is projected from
 * `draft_data->>'current_step'` and is `null` until a writer (6.3/6.5/6.7) persists it,
 * so this NEVER assumes a value: a known step id → its short label; any other non-null
 * string → capitalized as-is; `null` → a sensible label derived from the lifecycle
 * state (so the cell reads as intentional, never an empty "—"). No fabricated progress %
 * — the list contract carries no per-item counts (gotcha #6).
 */
export function humanizeCurrentStep(
  step: string | null | undefined,
  state: EvaluationState,
): string {
  if (step && STEP_SHORT_LABEL[step]) return STEP_SHORT_LABEL[step];
  if (step) return step.charAt(0).toUpperCase() + step.slice(1);
  // No `current_step` written yet — derive a sensible step from the lifecycle state so
  // the cell still reads as a step (mirrors Screen 12: a ready-to-score row sits at
  // "Review", a completed row reads "Completed").
  if (state === "scored") return "Completed";
  if (state === "submitted") return "Review";
  return "Draft started";
}
